import React, { useState, useEffect, useCallback } from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { useLanguage } from '../LanguageContext';
import { brokerAnalytics, brokerMemberLeads } from '../api';

const PERIODS = [
  ['today', 'اليوم', 'Today'],
  ['week', 'الأسبوع', 'Week'],
  ['month', 'الشهر', 'Month'],
  ['all', 'الكل', 'All'],
];

// status key -> [ar, en, color]
const STATUS = {
  new: ['جديد', 'New', '#8b5cf6'],
  contacted: ['تواصل', 'Contacted', '#3b82f6'],
  interested: ['مهتم', 'Interested', '#06b6d4'],
  site_visit_scheduled: ['زيارة مجدولة', 'Visit scheduled', '#0ea5e9'],
  site_visit_done: ['زيارة تمت', 'Visit done', '#14b8a6'],
  offer_sent: ['عرض مُرسل', 'Offer sent', '#f59e0b'],
  negotiating: ['تفاوض', 'Negotiating', '#eab308'],
  won: ['مكتسب', 'Won', '#16a34a'],
  lost: ['خسارة', 'Lost', '#dc2626'],
};

export default function BrokerAnalytics() {
  const { lang, isRTL } = useLanguage();
  const ar = lang === 'ar';
  const t = (a, e) => (ar ? a : e);
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // { member }

  const load = useCallback(async () => {
    setLoading(true);
    const res = await brokerAnalytics({ period });
    setData(res.error ? null : res);
    setLoading(false);
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const ov = data?.overall || {};
  const deals = data?.deals || {};
  const fmt = (n) => Number(n || 0).toLocaleString('en-US');
  const money = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
  const respLabel = (h, sample) => {
    if (!sample) return '—';
    if (h >= 48) return `${(h / 24).toFixed(1)}${ar ? ' يوم' : 'd'}`;
    return `${h.toFixed(1)}${ar ? ' س' : 'h'}`;
  };
  const stLabel = (k) => (STATUS[k] ? t(STATUS[k][0], STATUS[k][1]) : k);
  const stColor = (k) => (STATUS[k] ? STATUS[k][2] : '#64748b');

  const kpi = (label, value, color) => (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, flex: '1 1 120px', minWidth: 120 }}>
      <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: color || 'var(--text-primary)' }}>{value}</div>
    </div>
  );
  const cell = { padding: '9px 10px', borderBottom: '1px solid var(--border)', fontSize: '0.84rem', textAlign: 'center', whiteSpace: 'nowrap' };
  const nameCell = { ...cell, textAlign: isRTL ? 'right' : 'left', fontWeight: 600 };

  // Pipeline funnel: order statuses meaningfully, keep only those with a count.
  const pipeOrder = ['new', 'contacted', 'interested', 'site_visit_scheduled', 'site_visit_done', 'offer_sent', 'negotiating', 'won', 'lost'];
  const pipeMap = {};
  (data?.pipeline || []).forEach(p => { pipeMap[p.status] = p.count; });
  const pipeMax = Math.max(1, ...Object.values(pipeMap));
  const pipeRows = pipeOrder.filter(k => pipeMap[k] > 0);

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ maxWidth: 1040, margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {PERIODS.map(([k, a, e]) => (
          <button key={k} onClick={() => setPeriod(k)} style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid var(--border)', background: period === k ? 'var(--accent)' : 'var(--bg-secondary)', color: period === k ? '#fff' : 'var(--text-primary)', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
            {t(a, e)}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>...</div>
      ) : !data ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>{t('تعذّر تحميل البيانات', 'Could not load data')}</div>
      ) : (
        <>
          {/* KPI row */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
            {kpi(t('إجمالي العملاء', 'Total leads'), fmt(ov.total))}
            {kpi(t('غير متواصل معهم', 'Uncontacted'), fmt(ov.uncontacted), 'var(--warning, #f59e0b)')}
            {kpi(t('محتملون', 'Potential'), fmt(ov.potential), 'var(--accent)')}
            {kpi(t('مكتسب', 'Won'), fmt(ov.won), 'var(--success, #16a34a)')}
            {kpi(t('خسارة', 'Lost'), fmt(ov.lost), 'var(--danger, #dc2626)')}
            {kpi(t('نسبة التحويل', 'Conversion'), `${(ov.conversionRate || 0).toFixed(1)}%`)}
            {kpi(t('تم العمل عليهم', 'Worked'), fmt(ov.worked), 'var(--accent)')}
            {kpi(t('يحتاج متابعة', 'Needs follow-up'), fmt(ov.stale), 'var(--danger, #dc2626)')}
            {kpi(t('صفقات', 'Deals'), fmt(deals.count), 'var(--success, #16a34a)')}
            {kpi(t('العمولة (ر.س)', 'Commission (SAR)'), money(deals.commission), 'var(--success, #16a34a)')}
          </div>

          {/* Trend chart */}
          <div style={{ fontWeight: 800, fontSize: '0.95rem', margin: '8px 0 10px', color: 'var(--text-primary)' }}>{t('العملاء عبر الوقت', 'Leads over time')}</div>
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 24, height: 260 }}>
            {(data.trend || []).length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-secondary)' }}>{t('لا توجد بيانات', 'No data')}</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data.trend} margin={{ left: isRTL ? 10 : 0, right: isRTL ? 0 : 10, top: 10, bottom: 6 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" reversed={isRTL} stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} tickFormatter={(d) => String(d).slice(5)} />
                  <YAxis orientation={isRTL ? 'right' : 'left'} allowDecimals={false} stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: 'var(--bg-card, #1e2132)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="leads" name={t('عملاء جدد', 'New leads')} fill="var(--accent, #6366f1)" radius={[3, 3, 0, 0]} />
                  <Line type="monotone" dataKey="won" name={t('مكتسب', 'Won')} stroke="#16a34a" strokeWidth={2} dot={{ r: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Pipeline funnel */}
          {pipeRows.length > 0 && (
            <>
              <div style={{ fontWeight: 800, fontSize: '0.95rem', margin: '8px 0 10px', color: 'var(--text-primary)' }}>{t('مراحل العملاء', 'Pipeline')}</div>
              <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 24 }}>
                {pipeRows.map(k => (
                  <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div style={{ width: 110, fontSize: '0.82rem', color: 'var(--text-primary)', textAlign: isRTL ? 'right' : 'left' }}>{stLabel(k)}</div>
                    <div style={{ flex: 1, background: 'var(--bg-primary)', borderRadius: 6, overflow: 'hidden', height: 20 }}>
                      <div style={{ width: `${(pipeMap[k] / pipeMax) * 100}%`, background: stColor(k), height: '100%', borderRadius: 6, minWidth: 2 }} />
                    </div>
                    <div style={{ width: 44, textAlign: isRTL ? 'left' : 'right', fontSize: '0.84rem', fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(pipeMap[k])}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Team performance */}
          <div style={{ fontWeight: 800, fontSize: '0.95rem', margin: '8px 0 10px', color: 'var(--text-primary)' }}>{t('أداء الفريق', 'Team performance')} <span style={{ fontWeight: 500, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>· {t('اضغط على موظف لعرض عملائه', 'tap a member to see their leads')}</span></div>
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, overflowX: 'auto', marginBottom: 24 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead>
                <tr style={{ background: 'var(--bg-primary)' }}>
                  <th style={nameCell}>{t('الموظف', 'Employee')}</th>
                  <th style={cell}>{t('الإجمالي', 'Total')}</th>
                  <th style={cell}>{t('تم العمل', 'Worked')}</th>
                  <th style={cell}>{t('جديد', 'New')}</th>
                  <th style={cell}>{t('تواصل', 'Contacted')}</th>
                  <th style={cell}>{t('مهتم', 'Interested')}</th>
                  <th style={cell}>{t('زيارة', 'Visit')}</th>
                  <th style={cell}>{t('مكتسب', 'Won')}</th>
                  <th style={cell}>{t('خسارة', 'Lost')}</th>
                  <th style={cell}>{t('التحويل', 'Conv.')}</th>
                  <th style={cell}>{t('زمن الرد', 'Response')}</th>
                </tr>
              </thead>
              <tbody>
                {(data.salesPerformance || []).length === 0 ? (
                  <tr><td style={{ ...cell, textAlign: 'center' }} colSpan={11}>{t('لا توجد بيانات للفترة', 'No data for this period')}</td></tr>
                ) : data.salesPerformance.map((m) => (
                  <tr key={m.name} onClick={() => setDrill({ member: m.name })} style={{ cursor: 'pointer' }}>
                    <td style={{ ...nameCell, color: 'var(--accent)' }}>{m.name}</td>
                    <td style={cell}>{m.total}</td>
                    <td style={{ ...cell, color: 'var(--accent)', fontWeight: 700 }}>{m.worked || 0}</td>
                    <td style={cell}>{m.new}</td>
                    <td style={cell}>{m.contacted}</td>
                    <td style={cell}>{m.interested}</td>
                    <td style={cell}>{m.visitScheduled + m.visitDone}</td>
                    <td style={{ ...cell, color: 'var(--success, #16a34a)', fontWeight: 700 }}>{m.won}</td>
                    <td style={{ ...cell, color: 'var(--danger, #dc2626)' }}>{m.lost}</td>
                    <td style={cell}>{(m.conversionRate || 0).toFixed(0)}%</td>
                    <td style={cell}>{respLabel(m.avgResponseHours || 0, m.responseSample || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Loss reasons */}
          {(data.lossReasons || []).length > 0 && (
            <>
              <div style={{ fontWeight: 800, fontSize: '0.95rem', margin: '8px 0 10px', color: 'var(--text-primary)' }}>{t('أسباب الخسارة', 'Loss reasons')}</div>
              <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 24 }}>
                {data.lossReasons.map((l, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < data.lossReasons.length - 1 ? '1px solid var(--border)' : 'none', fontSize: '0.86rem' }}>
                    <span style={{ color: 'var(--text-primary)' }}>{l.reason}</span>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: 700 }}>{l.count}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {drill && <MemberDrill member={drill.member} onClose={() => setDrill(null)} isRTL={isRTL} t={t} stLabel={stLabel} stColor={stColor} />}
    </div>
  );
}

function MemberDrill({ member, onClose, isRTL, t, stLabel, stColor }) {
  const [leads, setLeads] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await brokerMemberLeads({ member });
      if (alive) setLeads(res.error ? [] : (res.leads || []));
    })();
    return () => { alive = false; };
  }, [member]);

  const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');
  const cell = { padding: '8px 10px', borderBottom: '1px solid var(--border)', fontSize: '0.82rem', whiteSpace: 'nowrap' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div dir={isRTL ? 'rtl' : 'ltr'} onClick={(e) => e.stopPropagation()} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 14, width: '100%', maxWidth: 760, maxHeight: '82vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{t('عملاء', 'Leads of')} · {member}{leads ? ` (${leads.length})` : ''}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ overflow: 'auto', padding: '0 6px' }}>
          {leads === null ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-secondary)' }}>...</div>
          ) : leads.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-secondary)' }}>{t('لا يوجد عملاء', 'No leads')}</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)' }}>
                  <th style={{ ...cell, textAlign: isRTL ? 'right' : 'left' }}>{t('الاسم', 'Name')}</th>
                  <th style={{ ...cell, textAlign: isRTL ? 'right' : 'left' }}>{t('الجوال', 'Phone')}</th>
                  <th style={{ ...cell, textAlign: 'center' }}>{t('الحالة', 'Status')}</th>
                  <th style={{ ...cell, textAlign: 'center' }}>{t('أُنشئ', 'Created')}</th>
                  <th style={{ ...cell, textAlign: 'center' }}>{t('المتابعة', 'Follow-up')}</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id}>
                    <td style={{ ...cell, textAlign: isRTL ? 'right' : 'left', fontWeight: 600 }}>{l.name || '—'}</td>
                    <td style={{ ...cell, textAlign: isRTL ? 'right' : 'left', direction: 'ltr' }}>{l.phone || '—'}</td>
                    <td style={{ ...cell, textAlign: 'center' }}><span style={{ color: stColor(l.status), fontWeight: 700 }}>{stLabel(l.status)}</span></td>
                    <td style={{ ...cell, textAlign: 'center', color: 'var(--text-secondary)' }}>{fmtDate(l.created_at)}</td>
                    <td style={{ ...cell, textAlign: 'center', color: 'var(--text-secondary)' }}>{fmtDate(l.follow_up_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
